/**
 * Riding the player's way: mount / dismount, seating, steering and the
 * per-type controllers for pig, strider, horse and donkey. Sits on top of the
 * shared mount model in game/riding.ts (`Rideable`, `ride`, `mountPlayer`), so
 * boats and minecarts use the very same seat bookkeeping.
 *
 * Wiring (main.ts):
 *  - `initMounts(player, world)` once;
 *  - right-click on a mob  -> `mountUse(mob, held, env)` (saddle / feed / mount);
 *  - right-click while riding -> `rideUseItem(held, env)` (carrot / fungus stick boost);
 *  - after `entities.tick()` -> `ridingAfterMobs()` (seat the rider, jump bar).
 * Rider input comes from `Player.rideInput` (the same keys the touch controls
 * inject): W/S/A/D, Space (jump / charge), Shift (dismount).
 */

import type { Player } from '../player/player';
import type { World } from '../world/chunk';
import { collisionOf, IS_WATERY } from '../blocks';
import type { Mob, MobCtx } from '../entities/mob';
import { Strider } from '../entities/nether/mobs2';
import { Animal } from '../entities/animals';
import { Horse, chargeScale, jumpVelocity, MAX_TEMPER, armorTierOf, HORSE_ARMOR } from '../entities/riding/horse';
import { I } from './itemIds';
import { mountPlayer, dismountPlayer, ride, PLAYER, type Rideable } from './riding';
import { isEmpty, type Stack } from './types';
import { registerMobExtra } from '../entities/persist';

/** Rider's feet above the mount's feet. */
export const SEAT_Y: Record<string, number> = { pig: 0.52, strider: 1.0, horse: 1.0, donkey: 0.9 };
export const STICK: Record<string, number> = { pig: I.CARROT_ON_A_STICK, strider: I.WARPED_FUNGUS_ON_A_STICK };
/** Durability a boost costs the steering stick. */
export const BOOST_COST: Record<string, number> = { pig: 7, strider: 1 };
/** Ridden ground speeds in blocks/tick. */
const PIG_SPEED = 0.17, STRIDER_LAVA = 0.2, STRIDER_COLD = 0.1;

export const rideableType = (t: string): boolean => t === 'pig' || t === 'strider' || t === 'horse' || t === 'donkey';

/** Per-ridden-mob transient state. */
interface RideData { boost: number; boostTotal: number; jumpTicks: number; jumpScale: number; crazy: number }
const data = new WeakMap<Mob, RideData>();
const rd = (m: Mob): RideData => { let d = data.get(m); if (!d) data.set(m, d = { boost: 0, boostTotal: 0, jumpTicks: 0, jumpScale: 0, crazy: 0 }); return d; };

export const mountState = {
  /** The mob the player sits on (null on foot). */
  mob: null as Mob | null,
  /** Jump-bar fill 0..1 while charging, -1 when not on a horse. */
  jumpBar: -1,
  /** Set when the mount threw the rider this tick (rearing). */
  thrown: 0,
  /** Hook the UI uses for toasts ("Press Shift to dismount"). */
  onMessage: null as ((msg: string) => void) | null,
};

/** A mob sitting-spot as a shared `Rideable`. */
export class MobMount implements Rideable {
  readonly id: number;
  readonly kind: string;
  readonly seatCount = 1;
  riders: (unknown | null)[] = [null];
  constructor(readonly mob: Mob) { this.id = mob.id; this.kind = mob.type; }
  seatPos(_i: number, out: { x: number; y: number; z: number }): void {
    out.x = this.mob.x; out.y = this.mob.y + (SEAT_Y[this.mob.type] ?? 0.8) * (this.mob.baby ? 0.5 : 1); out.z = this.mob.z;
  }
  dismountPos(out: { x: number; y: number; z: number }): void {
    const m = this.mob, w = world;
    out.x = m.x; out.y = m.y + 0.1; out.z = m.z;
    if (!w) return;
    const r = m.width / 2 + 0.8;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + 1;
      const x = m.x + Math.cos(a) * r, z = m.z + Math.sin(a) * r;
      for (const dy of [0, 1, -1]) {
        const by = Math.floor(m.y) + dy, bx = Math.floor(x), bz = Math.floor(z);
        if (collisionOf(w.getBlock(bx, by - 1, bz)).length > 0 && collisionOf(w.getBlock(bx, by, bz)).length === 0
          && collisionOf(w.getBlock(bx, by + 1, bz)).length === 0 && IS_WATERY[w.getBlock(bx, by, bz)] === 0) {
          out.x = x; out.y = by; out.z = z; return;
        }
      }
    }
  }
}

let player: Player | null = null;
let world: World | null = null;
const input = { fwd: 0, strafe: 0, jump: false, jumpTapped: false, sneak: false };
let rideAge = 0;
const tmp = { x: 0, y: 0, z: 0 };

export function initMounts(p: Player, w: World): void {
  player = p; world = w;
  p.rideTick = riderTick;
}
export function setMountWorld(w: World): void { world = w; }

/** Environment of one interaction (main.ts supplies the inventory side). */
export interface MountEnv {
  sneaking: boolean;
  consumeHeld(n: number): void;
  /** Open the horse inventory screen (null: not available). */
  openHorse?: (h: Horse) => void;
  effect?: (kind: 'heart' | 'poof', x: number, y: number, z: number) => void;
  rnd?: () => number;
  /** Server: is the clicking player already riding, and seat it on a mob (absent: the local player). */
  riding?: boolean;
  mount?: (mob: Mob) => boolean;
}

// ------------------------------------------------------------------ mount

/** Seat the local player on `mob`. False when already riding or not rideable. */
export function mountMobAsPlayer(mob: Mob): boolean {
  if (!player || ride.current || !mob.alive || !rideableType(mob.type) || mob.baby) return false;
  const seat = new MobMount(mob);
  if (!mountPlayer(seat)) return false;
  mob.rideCtl = (ctx) => control(mob, ctx);
  mountState.mob = mob;
  startRide(mob); // untamed horse decides in 1-2 s
  player.sitting = true;
  rideAge = 0;
  syncRider(true);
  mountState.onMessage?.('Press Shift to dismount');
  return true;
}

/** The player leaves its mount (sneak, mount death, deep water, ...). */
export function dismountRider(): void {
  const v = dismountPlayer();
  const p = player;
  const mob = mountState.mob;
  mountState.mob = null; mountState.jumpBar = -1;
  if (mob) mob.rideCtl = null;
  if (p) {
    p.sitting = false;
    if (v) { v.dismountPos(tmp); p.rideTo(tmp.x, tmp.y, tmp.z, true); }
    p.resetFall();
  }
}

export const isRiding = (): boolean => mountState.mob !== null;

function syncRider(snap = false): void {
  const m = mountState.mob;
  if (!m || !player) return;
  const seat = ride.current as MobMount | null;
  if (!seat) return;
  seat.seatPos(0, tmp);
  player.rideTo(tmp.x, tmp.y, tmp.z, snap);
  player.setBodyYaw(m.yaw);
}

/** Player.rideTick: runs inside the player's tick, before the mobs move. */
function riderTick(): void {
  const p = player, m = mountState.mob;
  if (!p || !m) { if (p) p.sitting = false; return; }
  p.rideInput(input);
  // sneak (Shift / touch sneak button) always gets you off; the click that mounted gets 2 ticks of grace
  if ((input.sneak && ++rideAge > 2) || !m.alive || m.removed || m.eyeInWater) { dismountRider(); return; }
  if (!input.sneak) rideAge = Math.max(rideAge, 3);
}

/** Call after entities.tick(): seats the rider on the moved mount and publishes the jump bar. */
export function ridingAfterMobs(): void {
  const m = mountState.mob;
  if (!m) return;
  if (!m.alive || m.removed) { dismountRider(); return; }
  syncRider();
  mountState.jumpBar = m instanceof Horse && m.tame && m.saddled ? rd(m).jumpScale : -1;
}

// ---------------------------------------------------------------- control

/** The mob's rideCtl: true = the rider steers this tick, false = the mob's own AI runs. */
function control(m: Mob, ctx: MobCtx): boolean {
  const p = player;
  if (!p) return false;
  LOCAL_RIDER.yaw = p.yaw; LOCAL_RIDER.fwd = input.fwd; LOCAL_RIDER.strafe = input.strafe; LOCAL_RIDER.jump = input.jump;
  LOCAL_RIDER.held = ctx.host.heldItem?.() ?? 0;
  return rideControl(m, ctx, LOCAL_RIDER);
}

/** Who steers a ridden mob this tick (the local player offline, any player on the server). */
export interface RiderCtl {
  yaw: number; fwd: number; strafe: number; jump: boolean;
  /** Item id in the rider's main hand (0 = empty). */
  held: number;
  /** The untamed horse threw the rider off. */
  thrown(m: Mob): void;
}

const LOCAL_RIDER: RiderCtl = {
  yaw: 0, fwd: 0, strafe: 0, jump: false, held: 0,
  thrown: (m) => { mountState.thrown++; if (mountState.mob === m) dismountRider(); },
};

/** A rider just sat down on `m`: reset the jump charge and (untamed horse) the 1-2 s until it decides. */
export function startRide(m: Mob, rnd: () => number = Math.random): void {
  const d = rd(m); d.jumpTicks = 0; d.jumpScale = 0;
  d.crazy = 20 + Math.floor(rnd() * 21);
}

/** Jump-bar fill 0..1 of a ridden mob. */
export const jumpCharge = (m: Mob): number => rd(m).jumpScale;

/** One tick of rider control over `m` (the body of a mob's `rideCtl`): true = the rider steers, false = its own AI runs. */
export function rideControl(m: Mob, ctx: MobCtx, r: RiderCtl): boolean {
  const yaw = r.yaw;
  const input = r;
  const d = rd(m);
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  if (m instanceof Strider) m.surface(ctx);

  if (m.type === 'pig' || m.type === 'strider') {
    const held = r.held;
    if (held !== STICK[m.type] || !m.saddled) return false; // no stick: it wanders where it likes
    m.setRideYaw(yaw);
    let speed = m.type === 'pig' ? PIG_SPEED : (m as Strider).onLava ? STRIDER_LAVA : STRIDER_COLD;
    if (d.boost > 0) {
      d.boost--;
      speed *= 1 + 1.15 * Math.sin((Math.PI * (d.boostTotal - d.boost)) / d.boostTotal);
    }
    // vanilla: with the stick held it walks where you look, no forward key
    m.setRideMove(fx, fz, speed / (0.546 * m.speed), false);
    return true;
  }

  const h = m as Horse;
  if (!(h instanceof Horse)) return false;
  m.setRideYaw(yaw);
  if (!h.tame) {
    // vanilla: after 1-2 s one attempt rolls 0..99 against the temper
    if (--d.crazy <= 0) {
      d.crazy = 1 << 30;
      if (h.tameRoll(ctx.host.rnd)) {
        for (let i = 0; i < 7; i++) ctx.host.effect('heart', m.x + (ctx.host.rnd() - 0.5), m.y + 1 + ctx.host.rnd(), m.z + (ctx.host.rnd() - 0.5));
      } else {
        for (let i = 0; i < 7; i++) ctx.host.effect('poof', m.x + (ctx.host.rnd() - 0.5), m.y + 1 + ctx.host.rnd(), m.z + (ctx.host.rnd() - 0.5));
        m.vy = 0.3;
        r.thrown(m);
      }
    }
    return true;
  }
  if (!h.saddled) return false; // tame but no saddle: wanders, no steering
  // movement: forward / strafe relative to the rider; backwards and sideways are slower
  if (input.fwd !== 0 || input.strafe !== 0) {
    const rx = Math.cos(yaw), rz = -Math.sin(yaw);
    let dx = fx * input.fwd + rx * input.strafe, dz = fz * input.fwd + rz * input.strafe;
    const l = Math.hypot(dx, dz) || 1; dx /= l; dz /= l;
    const k = (input.fwd < 0 ? 0.25 : 1) * (input.fwd === 0 ? 0.5 : 1);
    m.setRideMove(dx, dz, (h.hSpeed * 2.1 * k) / (0.546 * m.speed), false);
  }
  // jump charge: hold to fill the bar, release on the ground to leap
  if (input.jump) {
    d.jumpTicks++;
    d.jumpScale = chargeScale(d.jumpTicks);
  } else if (d.jumpTicks > 0) {
    if (m.onGround) m.vy = jumpVelocity(h.jumpStrength, d.jumpScale);
    d.jumpTicks = 0; d.jumpScale = 0;
  }
  return true;
}

// ------------------------------------------------------------ interaction

/**
 * Right-click on `mob` holding `held`: saddle it, feed a horse, equip armour,
 * open the horse screen (sneak) or climb on. True when the click was used.
 */
export function mountUse(mob: Mob, held: Stack, env: MountEnv): boolean {
  if ((env.mount ? env.riding : ride.current) || !rideableType(mob.type) || !mob.alive) return false;
  const mountIt = env.mount ?? mountMobAsPlayer;
  const id = isEmpty(held) ? 0 : held.id;
  if (mob instanceof Horse) {
    if (id && mob.feed(id, { effect: (k, x, y, z) => env.effect?.(k, x, y, z) })) { env.consumeHeld(1); return true; }
    if (mob.baby) return false;
    if (env.sneaking && mob.tame && env.openHorse) { env.openHorse(mob); return true; }
    if (id === I.SADDLE && mob.tame && !mob.saddled) { mob.saddled = true; env.consumeHeld(1); return true; }
    const tier = armorTierOf(id);
    if (tier > 0 && mob.tame && !mob.donkey && mob.armorTier === 0) { mob.armorTier = tier; env.consumeHeld(1); return true; }
    return mountIt(mob);
  }
  if (mob.type === 'pig' || mob.type === 'strider') {
    if (mob instanceof Animal && mob.baby) return false;
    if (id === I.SADDLE && !mob.saddled) { mob.saddled = true; env.consumeHeld(1); return true; }
    // a pig offered its food is fed, not ridden
    if (mob.type === 'pig' && (id === I.CARROT || id === I.POTATO || id === I.BEETROOT)) return false;
    if (mob.saddled && !env.sneaking) return mountIt(mob);
  }
  return false;
}

/**
 * Right-click while riding with `held`: the carrot / fungus stick boosts the
 * mount. `wear(n)` damages the held stick (true = it broke). True when used.
 */
export function rideUseItem(held: Stack, wear: (n: number) => boolean): boolean {
  const m = mountState.mob;
  if (!m || isEmpty(held)) return false;
  const stick = STICK[m.type];
  if (!stick || held.id !== stick || !m.saddled) return false;
  const d = rd(m);
  if (d.boost > 0) return false; // vanilla: no re-boost while boosting
  d.boostTotal = d.boost = 140 + Math.floor(Math.random() * 841);
  wear(BOOST_COST[m.type]);
  return true;
}

/** Boost state for tests / HUD. */
export const boostTicks = (m: Mob): number => rd(m).boost;
export const startBoost = (m: Mob, total: number): void => { const d = rd(m); d.boost = d.boostTotal = total; };

export { MAX_TEMPER, HORSE_ARMOR };
export { PLAYER };

// strider saddle survives a save (pig / horse go through Animal.saveExtra)
registerMobExtra('strider', {
  save: (m) => (m.saddled ? { sd: 1 } : undefined),
  load: (m, d) => { m.saddled = !!(d as { sd?: number } | null)?.sd; },
});
